import { Sizes } from '@anm/components/PlayerWrapper';
import ViewFormats from '@anm/components/ViewFormats';
import isMobileOrIPad from '@anm/helpers/is/isMobileOrIPad';
import { isDev } from '@anm/shared/mode';
import { useRef, FC } from 'react';
import { useSelector } from 'react-redux';
import * as semrush from '@anm/helpers/semrush';
import config from '../../../config';
import { WEBINAR_TABS } from '../../../pages/webinar';
import { landingsSelectors } from '../../../store/modules/landings';
import EmailThumb from '../../hosting/CustomizationSection/RightPanel/EmailThumb';
import EmbedPreview from '../../hosting/CustomizationSection/RightPanel/EmbedPreview';
import { LandingFrameWrapper, StickyWrapper } from '../../hosting/CustomizationSection/RightPanel/Wrapper';
import {
  useIframeCommunication,
  useIframeSizes,
  useLandingPreviewHover
} from '../../hosting/CustomizationSection/RightPanel/hooks';

import { PreviewWrapper, Wrapper } from './styles';

type WebinarPreviewProps = {
  title: string;
  sizes: Sizes;
  webinarId: string;
  activeButton: WEBINAR_TABS;
};

const WebinarPreview: FC<WebinarPreviewProps> = ({ sizes, title, webinarId, activeButton }) => {
  const canShowViewFormats = !isMobileOrIPad();
  const container = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const landing = useSelector(landingsSelectors.selectLanding);

  const [hoverRef, isHovered] = useLandingPreviewHover();
  const [iframeWidth, iframeHeight, viewType, handleTypeChange] = useIframeSizes(container, true);

  const isSemrushIframe = semrush.isSemrushIframe();

  const iframeSrc = isDev
    ? `http://localhost:8080/${webinarId}?target=customization&isSemrush=${isSemrushIframe}`
    : `${config().urls.webinarUrl}${webinarId}?target=customization&isSemrush=${isSemrushIframe}`;

  useIframeCommunication({
    viewType,
    iframeRef,
    landing
  });

  const isEmailScreen = activeButton === WEBINAR_TABS.email;
  const isEmbedScreen = activeButton === WEBINAR_TABS.embed;
  const isDomainsScreen = activeButton === WEBINAR_TABS.domains;

  const showIframe = !isEmailScreen && !isEmbedScreen && !isDomainsScreen;
  const isFullSizePreview = viewType === 'fullscreen';

  return (
    <Wrapper ref={container} {...{ isFullSizePreview }}>
      {canShowViewFormats && <ViewFormats {...{ isHovered }} onChange={handleTypeChange} selectedItem={viewType} />}

      <StickyWrapper ref={hoverRef}>
        {showIframe ? (
          <LandingFrameWrapper ref={iframeRef} src={iframeSrc} {...{ iframeHeight, iframeWidth }} />
        ) : (
          <PreviewWrapper>
            {isEmailScreen && <EmailThumb isWebinar={true} hostId={webinarId} sizes={sizes} />}
            {isEmbedScreen && (
              <EmbedPreview
                hostId={webinarId}
                videoName={title}
                renderInnerContent={() => <iframe src={`${iframeSrc}&embed=true`} />}
              />
            )}
          </PreviewWrapper>
        )}
      </StickyWrapper>
    </Wrapper>
  );
};

export default WebinarPreview;
